import { createT, type Translator } from '@freightapi/i18n'
import type { MessageParams } from '@freightapi/i18n/format'
import { SOURCE_LOCALE } from '@freightapi/i18n/namespaces'
import { createContext, type ReactNode, use, useMemo } from 'react'
import type { UiLocale } from './locale'

/**
 * Locale plumbing for the shared components (ADR 0010). The components never hardcode text; they
 * ask the nearest provider for a translator and for the locale to format dates and numbers with.
 *
 * Two catalogues are in play. `sign` holds the chrome these shells are authored against — section
 * titles, statuses, empty states. `pdf` holds the official CMR box labels, in the wording that is
 * printed on the paper consignment note, so a shipment rendered on screen is labelled exactly like
 * the document it stands for. The provider binds both to one locale, which is why every namespace
 * these components touch carries the same set of locales, French included.
 */
export type UiTranslator = Translator<'sign'>

/** The official CMR box labels and value wordings, bound to the same locale as {@link UiTranslator}. */
export type PdfTranslator = Translator<'pdf'>

/**
 * What a host app may hand the provider. Both apps that use these components render the consignment
 * note itself — sign since M2-01, the console since M3-04 — so both bind the `sign` catalogue and
 * the coupling is a plain type, not a union with a narrowing cast. A host still passes its own
 * translator rather than letting the provider build one, because the locale it resolved is the
 * locale the rest of its screen is already in.
 */
export type HostTranslator = Translator<'sign'>

export interface UiI18n {
  readonly locale: UiLocale
  readonly t: UiTranslator
  readonly pdf: PdfTranslator
}

/** Used when a component renders outside a provider: the source language, so text is never a key. */
const FALLBACK: UiI18n = {
  locale: SOURCE_LOCALE,
  t: createT('sign', SOURCE_LOCALE),
  pdf: createT('pdf', SOURCE_LOCALE),
}

const UiI18nContext = createContext<UiI18n>(FALLBACK)

export function UiI18nProvider({
  locale,
  translator,
  children,
}: {
  readonly locale: UiLocale
  /** Defaults to the `sign` catalogue in `locale`. */
  readonly translator?: HostTranslator
  readonly children: ReactNode
}) {
  const value = useMemo<UiI18n>(
    () => ({
      locale,
      t: translator ?? createT('sign', locale),
      // Never overridden: the box labels of a consignment note are the same document in every app.
      pdf: createT('pdf', locale),
    }),
    [locale, translator],
  )
  return <UiI18nContext value={value}>{children}</UiI18nContext>
}

export function useUiI18n(): UiI18n {
  return use(UiI18nContext)
}

/** Shorthand for the common case of needing only the translator. */
export function useT(): UiTranslator {
  return use(UiI18nContext).t
}

export type { MessageParams }
